/**
 * Which organisations I'm in, which one is active, and my roles there
 * (docs/engineering/ORG_ACCOUNTS_PLAN.md — S1, decisions D1, D9, D10, D11).
 *
 * On sign-in the provider calls `bootstrap` once: a new user gets their
 * organisation, and the server picks the remembered one. If the person came
 * through "Join an organisation" on the sign-in page, the code they typed is
 * waiting in sessionStorage — bootstrap is then told they are joining (so no
 * organisation of their own is made) and the code is used straight after.
 *
 * With FEATURE_ORGANISATIONS off, nothing here runs.
 *
 * This file holds only the context, its shape and the hook — no components —
 * and the provider lives in src/components/shell/OrgProvider.tsx. Keeping them
 * apart is load-bearing in development: a file that mixes a component with
 * other exports is fully re-created on hot reload, which made a NEW empty
 * context that freshly loaded pages read (sidebar without Manage until a
 * refresh, 30 Sep).
 */

import { createContext, useContext } from 'react'
import type { CodeJoin, MyOrg, OrgRole, PendingTransfer, WaitingInvite } from '@/lib/orgApi'

/** Set by the sign-in page's "Join an organisation"; read once after sign-in. */
export const PENDING_ORG_CODE_KEY = 'learnbee_pending_org_code'
/**
 * Set by /join/org/:token before sign-in. While it is there, bootstrap is told
 * the person is joining (D9) — someone signing up through an invitation gets
 * no organisation of their own. The invitation page clears it.
 */
export const PENDING_ORG_INVITE_KEY = 'learnbee_pending_org_invite'

export interface OrgsState {
  status: 'off' | 'loading' | 'ready' | 'error'
  error: string | null
  orgs: MyOrg[]
  current: MyOrg | null
  roles: OrgRole[]
  /** Owner or admin of the current organisation. */
  isManager: boolean
  /** Can create and edit courses in the current organisation. */
  isCreator: boolean
  /** Only a Learner here (no creating, no managing). */
  isLearnerOnly: boolean
  ownsOne: boolean
  /** This sign-in created the user's organisation — the app sends them to /org/setup once. */
  justCreated: boolean
  /** Setup was finished or skipped: stop sending them there. */
  markSetupDone: () => void
  /** Memberships that exist but are paused. */
  paused: Array<{ id: string; name: string }>
  /** Organisations they were removed from — explained when nothing else is left (G3). */
  removed: Array<{ id: string; name: string }>
  /** Organisations I asked to join with a code, waiting for an admin (15). */
  requested: Array<{ id: string; name: string; contactEmail?: string | null }>
  /** Organisations someone wants to hand to me — answered from the banner. */
  transfers: PendingTransfer[]
  /** Invitations to my addresses still waiting (the bell, and /org/invited). */
  invites: WaitingInvite[]
  /** First sign-in with invitations waiting: nothing created yet — the app sends them to /org/invited. */
  invitedFirst: boolean
  /** An existing user's own content moved into their organisation ON THIS sign-in (go-live, 25 Oct
   *  2026): what moved, for the one-time "What's new" pop-up. Null otherwise, and once it's seen. */
  adoptedNow: { courses: number; images: number; videos: number; pdfs: number } | null
  markWhatsNewSeen: () => void
  /** "Create your own organisation" from /org/invited. */
  createOwnOrg: () => Promise<void>
  switchTo: (orgId: string) => Promise<void>
  /** Use a code from inside the app: joined when invited, otherwise a request (see CodeJoin). */
  joinWithCode: (code: string) => Promise<CodeJoin>
  reload: () => Promise<void>
  /** Rename the current organisation locally after a successful save. */
  patchCurrent: (p: Partial<MyOrg>) => void
}

export const OrgsContext = createContext<OrgsState | null>(null)

const OFF: OrgsState = {
  status: 'off', error: null, orgs: [], current: null, roles: [], isManager: false, isCreator: true,
  isLearnerOnly: false, ownsOne: false, justCreated: false, markSetupDone: () => {}, paused: [], removed: [], requested: [], transfers: [],
  invites: [], invitedFirst: false, adoptedNow: null, markWhatsNewSeen: () => {}, createOwnOrg: async () => {},
  switchTo: async () => {}, joinWithCode: async () => { throw new Error('Organisations are off') },
  reload: async () => {}, patchCurrent: () => {},
}

export function useOrgs(): OrgsState {
  return useContext(OrgsContext) ?? OFF
}
